import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MessageSquare, Search, Sparkles } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Composeur from '../../components/conversation/Composeur';
import ToastContainer from '../../components/ui/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { type Agent, useAgents } from '../../hooks/useAgents';
import { authManager } from '../../services/authManager';
import { assistantsService, type ResumeConversation } from '../../services/api/assistants';

/** État transmis à l'écran de conversation pour qu'il envoie le premier message. */
export interface DemarrageConversation {
  premierMessage: string;
  automatique: boolean;
  raison: string;
}

const salutation = () => {
  const heure = new Date().getHours();
  return heure < 18 ? 'Bonjour' : 'Bonsoir';
};

const periode = (valeur: string) => {
  const date = new Date(valeur);
  const maintenant = new Date();
  const jours = Math.floor((maintenant.getTime() - date.getTime()) / 86_400_000);
  if (date.toDateString() === maintenant.toDateString()) return "Aujourd'hui";
  if (jours < 7) return 'Cette semaine';
  if (jours < 31) return 'Ce mois-ci';
  return 'Plus ancien';
};

interface Props {
  /** Nouvelle communication : seuls les agents de communication sont proposés et choisis. */
  communication?: boolean;
}

/**
 * Point d'arrivée de la plateforme.
 *
 * On commence par écrire, comme dans un assistant de conversation : l'agent
 * adapté est choisi d'après le message, ou désigné par « @ ». L'historique des
 * échanges est juste en dessous.
 *
 * La même page sert les nouvelles communications : une partie des équipes ne
 * fait que des supports pour le public et n'a pas à traverser les budgets pour
 * y arriver. Le choix de l'agent se fait alors parmi ceux de la communication.
 */
const ConversationsPage: React.FC<Props> = ({ communication = false }) => {
  const navigate = useNavigate();
  const { toasts, removeToast, showError } = useToast();
  const { agents: catalogue } = useAgents();
  const agents = useMemo(
    () => (communication ? catalogue.filter((a) => a.communication) : catalogue),
    [catalogue, communication],
  );
  const [conversations, setConversations] = useState<ResumeConversation[]>([]);
  const [chargement, setChargement] = useState(true);
  const [saisie, setSaisie] = useState('');
  const [agent, setAgent] = useState<Agent | null>(null);
  const [demarrage, setDemarrage] = useState(false);
  const [recherche, setRecherche] = useState('');

  useEffect(() => {
    assistantsService
      .listerConversations()
      .then(setConversations)
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  const compte = authManager.getUser();
  const prenom = compte?.first_name || (compte?.full_name || '').split(' ')[0] || '';

  const demarrer = async () => {
    const texte = saisie.trim();
    if (!texte || demarrage) return;
    setDemarrage(true);
    try {
      const ouverte = await assistantsService.demarrerConversation(
        texte, agent?.id, null, communication ? 'communication' : undefined,
      );
      const etat: DemarrageConversation = {
        premierMessage: texte,
        automatique: ouverte.automatique,
        raison: ouverte.raison,
      };
      navigate(`/workspace/conversation/${ouverte.id}`, { state: etat });
    } catch (erreur: any) {
      showError('Conversation impossible', erreur.message);
      setDemarrage(false);
    }
  };

  // Quelques agents proposés d'emblée : ceux qui ouvrent le catalogue, sans
  // l'échange libre, qui est le choix par défaut.
  const suggeres = useMemo(
    () => agents
      .filter((a) => !['echange', 'general'].includes(a.category_code || ''))
      .slice(0, communication ? 8 : 5),
    [agents, communication],
  );

  // En communication, l'historique ne montre que les échanges avec ces agents.
  const historique = useMemo(() => {
    if (!communication) return conversations;
    const ids = new Set(agents.map((a) => a.id));
    return conversations.filter((c) => ids.has(c.template));
  }, [conversations, agents, communication]);

  const filtrees = useMemo(() => {
    const cle = recherche.trim().toLowerCase();
    return historique.filter(
      (c) =>
        !cle
        || (c.title || '').toLowerCase().includes(cle)
        || (c.template_title || '').toLowerCase().includes(cle),
    );
  }, [historique, recherche]);

  const groupes = useMemo(() => {
    const ordre = ["Aujourd'hui", 'Cette semaine', 'Ce mois-ci', 'Plus ancien'];
    const parPeriode: Record<string, ResumeConversation[]> = {};
    filtrees.forEach((c) => {
      const cle = periode(c.updated_at);
      (parPeriode[cle] = parPeriode[cle] || []).push(c);
    });
    return ordre.filter((cle) => parPeriode[cle]).map((cle) => ({ cle, liste: parPeriode[cle] }));
  }, [filtrees]);

  const heure = (valeur: string) =>
    new Date(valeur).toLocaleString('fr-FR', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });

  return (
    <DashboardLayout>
      {/* Le haut de l'écran porte les couleurs du logo : bleu pétrole et
          vert sauge, comme la page d'accueil. */}
      <div
        className="relative z-10 -mx-4 -mt-4 bg-[linear-gradient(165deg,#365B6D_0%,#2E5061_55%,#284756_100%)]
          px-4 pb-12 text-white lg:-mx-8 lg:-mt-7 lg:px-8"
      >
        {/* Le halo est rogné dans son propre cadre : rogner tout le bandeau
            couperait la liste des agents, qui s'ouvre vers le bas. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-48 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full
              bg-[radial-gradient(closest-side,rgba(108,146,134,0.45),transparent)]"
          />
        </div>
      <div className="relative mx-auto max-w-[760px]">
        <div className="pb-8 pt-12 text-center sm:pt-16">
          <h1 className="text-[30px] font-semibold tracking-tight text-white sm:text-[34px]">
            {communication ? 'Nouvelle communication' : `${salutation()}${prenom ? `, ${prenom}` : ''}`}
          </h1>
          <p className="mt-2 text-[15px] text-white/75">
            {communication
              ? "Une affiche, un spot radio, une capsule vidéo… L'agent de communication adapté prend la demande."
              : "De quoi avez-vous besoin pour le programme aujourd'hui ?"}
          </p>
        </div>

        <Composeur
          agents={agents}
          agent={agent}
          onAgent={setAgent}
          valeur={saisie}
          onChange={setSaisie}
          onEnvoyer={demarrer}
          occupe={demarrage}
          large
          autoFocus
          placeholder={communication
            ? 'Décrivez le support : public, message, langue, format… Tapez @ pour choisir un agent.'
            : 'Posez une question, demandez un document, un budget, un visuel… Tapez @ pour choisir un agent.'}
        />

        {demarrage && (
          <div className="mt-3 flex items-center justify-center gap-2 text-[12.5px] text-white/80">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[#9CC0B3]" />
            {agent
              ? `Ouverture de l'échange avec ${agent.title}…`
              : communication ? "Choix de l'agent de communication…" : "Choix de l'agent adapté à votre demande…"}
          </div>
        )}

        {!demarrage && suggeres.length > 0 && (
          <div className="mt-4 flex flex-wrap justify-center gap-1.5">
            {suggeres.map((suggere) => (
              <button
                key={suggere.id}
                type="button"
                onClick={() => setAgent(suggere)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px]
                  font-medium transition-colors ${
                    agent?.id === suggere.id
                      ? 'bg-eg-sauge text-white'
                      : 'bg-white/10 text-white/90 outline outline-1 -outline-offset-1 outline-white/15 hover:bg-white/[0.16]'
                  }`}
              >
                <Sparkles className="h-3 w-3 opacity-70" />
                {suggere.title}
              </button>
            ))}
          </div>
        )}

      </div>
      </div>

      <div className="mx-auto max-w-[760px] pb-16">
        <section className="mt-10">
          <div className="mb-3 flex items-center gap-3">
            <h2 className="text-[15px] font-medium text-eg-accent">
              {communication ? 'Mes communications' : 'Mes conversations'}
            </h2>
            <span className="text-[12px] text-eg-estompe">{historique.length}</span>
            <div className="relative ml-auto w-56">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-eg-estompe" />
              <input
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                placeholder="Rechercher"
                className="w-full rounded-full border-0 bg-black/[0.045] py-1.5 pl-8 pr-3 text-[12.5px]
                  placeholder:text-eg-estompe focus:bg-eg-blanc focus:outline-none focus:ring-2
                  focus:ring-eg-accent/20"
              />
            </div>
          </div>

          {chargement ? (
            <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-eg-gris">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : filtrees.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-eg-bord
              py-12 text-center text-eg-gris">
              <MessageSquare className="h-6 w-6 text-eg-estompe" />
              <p className="text-[13px]">
                {recherche
                  ? 'Aucune conversation ne correspond.'
                  : communication ? 'Aucune communication pour le moment.' : 'Aucune conversation pour le moment.'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {groupes.map(({ cle, liste }) => (
                <div key={cle}>
                  <div className="mb-1.5 px-1 text-[11.5px] font-medium text-eg-estompe">{cle}</div>
                  <div className="overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1
                    -outline-offset-1 outline-black/[0.07]">
                    {liste.map((conversation, index) => (
                      <button
                        key={conversation.id}
                        type="button"
                        onClick={() => navigate(`/workspace/conversation/${conversation.id}`)}
                        className={`flex w-full items-center gap-4 px-4 py-3 text-left transition-colors
                          hover:bg-eg-papier ${index > 0 ? 'border-t border-eg-bordure' : ''}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13.5px] font-medium text-eg-encre">
                            {conversation.title || conversation.template_title}
                          </p>
                          <p className="mt-0.5 truncate text-[12px] text-eg-gris">
                            {conversation.template_title}
                          </p>
                        </div>
                        <span className="shrink-0 font-mono text-[11px] text-eg-estompe">
                          {heure(conversation.updated_at)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </DashboardLayout>
  );
};

export default ConversationsPage;
